import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, ExternalLink } from 'lucide-react';
import { portal } from '../api';
import type { BillingCycle, Contract, ProductPlan } from '../types';

export default function ChangePlanModal({
  contract,
  plans,
  onClose,
}: {
  contract: Contract;
  plans: ProductPlan[];
  onClose: () => void;
}) {
  const otherPlans = plans.filter((p) => p.id !== contract.planId);
  const [planId, setPlanId] = useState(otherPlans[0]?.id ?? '');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [error, setError] = useState('');

  const changeMutation = useMutation({
    mutationFn: () => portal.changePlan(contract.id, { planId, billingCycle }),
    onSuccess: ({ checkoutUrl }) => {
      window.location.href = checkoutUrl;
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Tarifwechsel fehlgeschlagen.';
      setError(msg);
    },
  });

  const currentPrice = Number(contract.plan?.monthlyPrice ?? 0);
  const selectedPlan = plans.find((p) => p.id === planId);
  const newPrice = Number(selectedPlan?.monthlyPrice ?? 0);
  const direction = newPrice >= currentPrice ? 'Upgrade' : 'Downgrade';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-md max-h-screen overflow-y-auto">
        <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Tarif wechseln</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Aktuell: {contract.plan?.name ?? '—'}
              {contract.plan?.monthlyPrice && ` (€${contract.plan.monthlyPrice}/Monat)`}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            changeMutation.mutate();
          }}
          className="px-6 py-5 space-y-5"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Neuer Tarif</label>
            <div className="space-y-2">
              {otherPlans.map((plan) => (
                <label
                  key={plan.id}
                  className={`flex items-center justify-between border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                    planId === plan.id
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <input
                      type="radio"
                      name="newplan"
                      value={plan.id}
                      checked={planId === plan.id}
                      onChange={() => setPlanId(plan.id)}
                      className="accent-brand-600"
                    />
                    <span className="text-sm font-medium text-slate-900">{plan.name}</span>
                  </div>
                  <span className="text-sm text-slate-500">
                    {plan.monthlyPrice && <>€{plan.monthlyPrice}/Mon.</>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Abrechnung</label>
            <div className="flex gap-2">
              {(['monthly', 'yearly'] as BillingCycle[]).map((cycle) => {
                const available =
                  cycle === 'yearly' ? selectedPlan?.yearlyPrice : selectedPlan?.monthlyPrice;
                return (
                  <button
                    key={cycle}
                    type="button"
                    disabled={!available}
                    onClick={() => setBillingCycle(cycle)}
                    className={`flex-1 text-sm py-2 rounded-lg border transition-colors disabled:opacity-40 ${
                      billingCycle === cycle
                        ? 'border-brand-500 bg-brand-50 text-brand-800 font-medium'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {cycle === 'monthly' ? 'Monatlich' : 'Jährlich'}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedPlan && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Art der Änderung</span>
                <span className="font-medium text-slate-900">{direction}</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Die Abrechnung der Differenz erfolgt über den Checkout.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={changeMutation.isPending || !planId}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg disabled:opacity-60"
            >
              {changeMutation.isPending ? 'Weiterleitung…' : 'Wechsel bestätigen'}
              <ExternalLink size={14} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
